import { useCallback, useState } from 'react';
import { NotifiableError } from '@bugsnag/js';
import { bugtracker } from '@99designs/design-services-common';
import { DS_BRIEF_MULTI_STEP_URL, __, __url } from '@99designs/i18n';
import { useBriefConfig } from '../../context';
import { ClaimBriefMutation, useClaimBriefMutation, useSubmitRequestMutation } from '../../graph';
import { DuplicateBriefPurchaseError } from '../../lib/errors';
import { canUseCart } from '../../lib/hooks/useCart';
import { useNavigateWithLang } from '../../lib/hooks/useNavigateWithLang';
import { useAddBriefToCart } from './useAddBriefToCart';
import { useSubmitToRequests } from './useSubmitToRequests';

interface HandleSubmitProps {
    briefId: string;
    locale: string;
    accessToken: string;
    shopperId: string;
    willSkipCart: boolean;
}

export type SubmitState =
    | 'idle'
    | 'submitting'
    | 'duplicate'
    | 'invalid-shopper'
    | 'success'
    | 'error';

export interface UseHandleSubmitReturn {
    state: SubmitState;
    loadingCopy: string;
    currentUrl: string;
    mpvId: string;
    handleSubmit: () => Promise<void>;
}

export const useHandleSubmit = ({
    briefId,
    locale,
    accessToken,
    shopperId,
    willSkipCart,
}: HandleSubmitProps): UseHandleSubmitReturn => {
    const [state, setState] = useState<SubmitState>('idle');
    const [loadingCopy, setLoadingCopy] = useState(
        willSkipCart ? '' : __('Adding project to your cart')
    );
    const [currentUrl, setCurrentUrl] = useState('');
    const [mpvId, setMpvId] = useState('');

    const navigate = useNavigateWithLang();
    const addBriefToCart = useAddBriefToCart(locale, accessToken, shopperId, briefId); // Community
    const submitToRequests = useSubmitToRequests(locale, accessToken, shopperId, briefId); // CARE Collaborate
    const [submitRequest] = useSubmitRequestMutation(); // Upload&go
    const briefConfig = useBriefConfig();
    const [claimBriefMutation] = useClaimBriefMutation({
        variables: { id: briefId },
    });

    const handleSubmit = useCallback(async () => {
        try {
            setState('submitting');

            if (!canUseCart(shopperId)) {
                const copy = `Users with internal shopper ids like ${shopperId} cannot checkout. Please log out and log back in using a shopper account`;
                alert(copy);
                setLoadingCopy(copy);
                setState('invalid-shopper');
                return;
            }

            if (!willSkipCart) {
                setLoadingCopy(__('Taking you to your cart'));
            }

            const result = await claimBriefMutation();
            const data = result.data;
            if (!data) throw new Error(`Failed to call claimBriefMutation: ${result.errors}`);

            if (isMultiBriefPurchaseAttempt(data)) {
                bugtracker.notify(
                    new DuplicateBriefPurchaseError(
                        `Encountered duplicate purchase attempt for brief in useHandleSubmit hook: ${briefId}`
                    )
                );

                setState('duplicate');
                setMpvId(data.claimStructuredBrief.fulfilment?.mpvId ?? '');
                setCurrentUrl(data.claimStructuredBrief.request?.facts?.currentUrl ?? '');

                return;
            }

            if (isBifrostBrief(data)) {
                await addBriefToCart(data);
                setState('success');
                window.location.replace(__url('/c'));
                return;
            }
            if (isUploadAndGoBrief(data)) {
                if (!data.claimStructuredBrief.request?.id) {
                    throw new Error('Cannot submit request without requestId');
                }
                const { data: requestsData } = await submitRequest({
                    variables: {
                        requestId: data.claimStructuredBrief.request.id,
                    },
                });

                if (!requestsData) throw new Error(`Failed to call to requests`);

                setState('success');

                if (briefConfig.autoFulfilment) {
                    window.location.href = `http://localhost:3001${requestsData?.submitRequest?.facts?.currentUrl}`;
                }

                window.location.replace(
                    requestsData?.submitRequest?.facts?.currentUrl ||
                        __url('/graphic-design/dashboard')
                );

                return;
            }

            await submitToRequests(data); // Fulfil via Requests if Requests provisioned the brief.
            setState('success');
            if (!briefConfig.autoFulfilment) {
                window.location.replace(__url('/c'));
            }
            return;
        } catch (e) {
            bugtracker.notify(e as NotifiableError, (event) => {
                event.context = `Error during brief submission for briefId: ${briefId}.`;
            });
            setState('error');
            navigate(DS_BRIEF_MULTI_STEP_URL + '/error');
            throw e;
        }
    }, [
        briefId,
        claimBriefMutation,
        addBriefToCart,
        submitToRequests,
        submitRequest,
        willSkipCart,
        shopperId,
        briefConfig,
        navigate,
    ]);

    return {
        state,
        loadingCopy,
        currentUrl,
        mpvId,
        handleSubmit,
    };
};
function isBifrostBrief(data: ClaimBriefMutation) {
    // Bifrost briefs will not have a request object and will also have a fulfilment strategy of COMMUNITY
    // There may be some old briefs from before RnB that don't have a request object but are not Bifrost briefs
    return (
        data.claimStructuredBrief.request == null &&
        data.claimStructuredBrief.fulfilmentStrategy === 'FULFILMENT_STRATEGY_COMMUNITY'
    );
}
function isUploadAndGoBrief(data: ClaimBriefMutation) {
    return data.claimStructuredBrief.request?.facts?.workflow === 'CARE_EXPRESS';
}

function isMultiBriefPurchaseAttempt(data: ClaimBriefMutation) {
    // A brief is considered purchased if it has an order associated with it (Bifrost) or if it has a request that is not in DRAFT or IN-PROGRESS state (RnB)
    return (
        (isBifrostBrief(data) && data.claimStructuredBrief.order?.id != null) ||
        (data.claimStructuredBrief.request != null &&
            data.claimStructuredBrief.request?.state !== 'DRAFT' &&
            data.claimStructuredBrief.request?.state !== 'IN_PROGRESS')
    );
}
